import type { MailAuthAudit } from "./types";

const checkedAt = "2026-06-14T08:42:17.000Z";

export const sampleAudit: MailAuthAudit = {
  domain: "example.com",
  checkedAt,
  score: 88,
  scoreLabel: "Good",
  queries: {
    spfTxt: {
      name: "example.com",
      type: "TXT",
      status: 0,
      answers: [
        { name: "example.com", type: 16, ttl: 3600, data: "v=spf1 include:_spf.example.net -all" }
      ],
      error: ""
    },
    dmarcTxt: {
      name: "_dmarc.example.com",
      type: "TXT",
      status: 0,
      answers: [
        { name: "_dmarc.example.com", type: 16, ttl: 3600, data: "v=DMARC1; p=quarantine; rua=mailto:dmarc@example.com" }
      ],
      error: ""
    },
    mx: {
      name: "example.com",
      type: "MX",
      status: 0,
      answers: [
        { name: "example.com", type: 15, ttl: 3600, data: "10 mx1.example.net." },
        { name: "example.com", type: 15, ttl: 3600, data: "20 mx2.example.net." }
      ],
      error: ""
    },
    bimiTxt: {
      name: "default._bimi.example.com",
      type: "TXT",
      status: 0,
      answers: [
        { name: "default._bimi.example.com", type: 16, ttl: 3600, data: "v=BIMI1; l=https://example.com/bimi/logo.svg; a=https://example.com/bimi/vmc.pem" }
      ],
      error: ""
    },
    mtaStsTxt: {
      name: "_mta-sts.example.com",
      type: "TXT",
      status: 0,
      answers: [
        { name: "_mta-sts.example.com", type: 16, ttl: 3600, data: "v=STSv1; id=2026061401" }
      ],
      error: ""
    },
    tlsRptTxt: {
      name: "_smtp._tls.example.com",
      type: "TXT",
      status: 0,
      answers: [
        { name: "_smtp._tls.example.com", type: 16, ttl: 3600, data: "v=TLSRPTv1; rua=mailto:tlsrpt@example.com" }
      ],
      error: ""
    }
  },
  spf: {
    status: "pass",
    records: ["v=spf1 include:_spf.example.net -all"],
    mechanisms: ["include:_spf.example.net", "-all"],
    lookupCount: 1,
    allMode: "-all",
    findings: [
      { level: "pass", title: "Single SPF record", detail: "The domain publishes one SPF policy." },
      { level: "pass", title: "Strict sender boundary", detail: "The policy ends with -all." }
    ]
  },
  dmarc: {
    status: "pass",
    records: ["v=DMARC1; p=quarantine; rua=mailto:dmarc@example.com"],
    tags: { v: "DMARC1", p: "quarantine", rua: "mailto:dmarc@example.com" },
    policy: "quarantine",
    subdomainPolicy: "inherit",
    reporting: "mailto:dmarc@example.com",
    alignment: "relaxed / relaxed",
    findings: [
      { level: "pass", title: "DMARC is enforced", detail: "The policy is set to quarantine." },
      { level: "pass", title: "Aggregate reports enabled", detail: "rua reporting is present." }
    ]
  },
  mx: {
    status: "pass",
    hosts: [
      { priority: 10, host: "mx1.example.net" },
      { priority: 20, host: "mx2.example.net" }
    ],
    findings: [{ level: "pass", title: "MX records found", detail: "Two mailbox hosts are published." }]
  },
  bimi: {
    status: "pass",
    records: ["v=BIMI1; l=https://example.com/bimi/logo.svg; a=https://example.com/bimi/vmc.pem"],
    logoUrl: "https://example.com/bimi/logo.svg",
    certificateUrl: "https://example.com/bimi/vmc.pem",
    findings: [{ level: "pass", title: "BIMI candidate found", detail: "The record includes a logo URL and certificate URL." }]
  },
  mtaSts: {
    status: "pass",
    txtRecords: ["v=STSv1; id=2026061401"],
    policyUrl: "https://mta-sts.example.com/.well-known/mta-sts.txt",
    policyFound: true,
    policyFields: {
      version: "STSv1",
      mode: "enforce",
      mx: "mx1.example.net, mx2.example.net",
      max_age: "604800"
    },
    policyText: "version: STSv1\nmode: enforce\nmx: mx1.example.net\nmx: mx2.example.net\nmax_age: 604800",
    findings: [{ level: "pass", title: "MTA-STS enforce policy", detail: "The HTTPS policy is reachable and uses enforce mode." }]
  },
  tlsRpt: {
    status: "pass",
    records: ["v=TLSRPTv1; rua=mailto:tlsrpt@example.com"],
    rua: "mailto:tlsrpt@example.com",
    findings: [{ level: "pass", title: "TLS reports enabled", detail: "The domain publishes a TLS-RPT destination." }]
  },
  dkim: [
    {
      selector: "selector1",
      status: "pass",
      records: ["v=DKIM1; k=rsa; p=MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQC..."],
      keyType: "rsa",
      hasPublicKey: true,
      findings: [{ level: "pass", title: "DKIM selector found", detail: "The selector publishes a public key." }]
    }
  ],
  checklist: [
    { key: "spf", label: "SPF exists", level: "pass", detail: "SPF is required or expected for sender authorization." },
    { key: "dkim", label: "DKIM selector checked", level: "pass", detail: "The provided selector publishes a key." },
    { key: "dmarc", label: "DMARC exists", level: "pass", detail: "DMARC policy is published." },
    { key: "policy", label: "DMARC policy not open", level: "pass", detail: "Policy is stronger than p=none." },
    { key: "mx", label: "MX present", level: "pass", detail: "Mailbox routing records exist." },
    { key: "mta", label: "MTA-STS policy", level: "pass", detail: "Transport policy is reachable." },
    { key: "tls", label: "TLS-RPT reporting", level: "pass", detail: "TLS report destination exists." },
    { key: "bimi", label: "BIMI candidate", level: "pass", detail: "Brand indicator record is present." }
  ],
  recommendations: [
    { level: "warn", title: "Move to reject after monitoring", action: "Consider p=reject once reports confirm legitimate traffic." },
    { level: "info", title: "Keep DKIM inventory current", action: "Check every active ESP selector before major campaigns." },
    { level: "info", title: "Review reporting mailboxes", action: "Make sure DMARC and TLS-RPT addresses are monitored." }
  ],
  generated: {
    spf: "v=spf1 include:_spf.example.net include:mail.example.com -all",
    dmarc: "v=DMARC1; p=quarantine; sp=quarantine; adkim=s; aspf=s; rua=mailto:dmarc@example.com; ruf=mailto:dmarc-forensic@example.com; fo=1; t=86400; np=reject; psd=y",
    mtaStsTxt: "v=STSv1; id=2026061401",
    mtaStsPolicy: "version: STSv1\nmode: enforce\nmx: mx1.example.net\nmx: mx2.example.net\nmax_age: 604800",
    tlsRpt: "v=TLSRPTv1; rua=mailto:tlsrpt@example.com",
    bimi: "v=BIMI1; l=https://example.com/bimi/logo.svg; a=https://example.com/bimi/vmc.pem",
    markdown:
      "# DNS Change Request for example.com\n\n| Type | Name | Value | TTL | Purpose |\n| --- | --- | --- | ---: | --- |\n| TXT | @ | v=spf1 include:_spf.example.net include:mail.example.com -all | 3600 | SPF sender policy |\n| TXT | _dmarc | v=DMARC1; p=quarantine; sp=quarantine; adkim=s; aspf=s; rua=mailto:dmarc@example.com | 3600 | DMARC policy |\n| TXT | _mta-sts | v=STSv1; id=2026061401 | 3600 | MTA-STS discovery |\n| TXT | _smtp._tls | v=TLSRPTv1; rua=mailto:tlsrpt@example.com | 3600 | TLS reports |"
  }
};
